/**
 * Date utility functions for DatePicker component
 */

export type DateDisplayFormat = "long" | "numeric";

/**
 * Format date for display in the input
 * @param date - The date to format
 * @param showTime - Whether to include time in the format
 * @param dateFormat - Display format for the date portion
 */
export const formatDate = (
	date: Date | undefined,
	showTime: boolean = false,
	dateFormat: DateDisplayFormat = "long"
): string => {
	if (!date || !isValidDate(date) || isNaN(date.getTime())) {
		return "";
	}

	const dateStr =
		dateFormat === "numeric"
			? date.toLocaleDateString("en-US", {
					day: "2-digit",
					month: "2-digit",
					year: "numeric",
				})
			: date.toLocaleDateString("en-US", {
					day: "numeric",
					month: "long",
					year: "numeric",
				});

	if (showTime) {
		const timeStr = date.toLocaleTimeString("en-US", {
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		});
		return `${dateStr} ${timeStr}`;
	}

	return dateStr;
};

/**
 * Check if a date is valid
 */
export const isValidDate = (date: Date | undefined): boolean => {
	if (!date) {
		return false;
	}
	return !isNaN(date.getTime());
};

/**
 * Parse date from various string formats
 */
export const parseDate = (value: string): Date | undefined => {
	if (!value.trim()) {
		return undefined;
	}

	// Try parsing the value as a date
	const date = new Date(value);
	return isValidDate(date) ? date : undefined;
};

/**
 * Validate date against min/max constraints
 */
export const isDateInRange = (date: Date, startDate?: Date, endDate?: Date): boolean => {
	if (startDate && date.getTime() < startDate.getTime()) {
		return false;
	}
	if (endDate && date.getTime() > endDate.getTime()) {
		return false;
	}
	return true;
};

/**
 * Whether a typed value carries a time of day, e.g. "June 1, 2027 5:00 PM"
 */
export const hasTimeComponent = (value: string): boolean => /\d\s*:\s*\d|\d\s*(am|pm)\b/i.test(value);

/**
 * Process and validate user input, returning the processed result
 *
 * `timeOfDay` is the hour to apply when the typed value is a date with no time of day, so a typed
 * date lands on the same hour the calendar would have given it instead of on midnight. Leave it
 * undefined to keep the parsed value as-is.
 */
export const processDateInput = (
	inputValue: string,
	currentValue: Date | null | undefined,
	startDate?: Date,
	endDate?: Date,
	allowEmpty?: boolean,
	timeOfDay?: number
): {
	isValid: boolean;
	date: Date | null;
	shouldReset: boolean;
} => {
	// Handle empty input
	if (inputValue === "") {
		return {
			isValid: true,
			date: allowEmpty ? null : currentValue || null,
			shouldReset: !allowEmpty,
		};
	}

	// Parse the date
	const parsedDate = parseDate(inputValue);
	if (!parsedDate || !isValidDate(parsedDate)) {
		return {
			isValid: false,
			date: currentValue || null,
			shouldReset: true,
		};
	}

	// Apply the hour before the range check, so the validated date is the one we return
	const date = new Date(parsedDate);
	if (timeOfDay !== undefined && !hasTimeComponent(inputValue)) {
		date.setHours(timeOfDay, 0, 0, 0);
	}

	// Validate range
	if (!isDateInRange(date, startDate, endDate)) {
		return {
			isValid: false,
			date: currentValue || null,
			shouldReset: true,
		};
	}

	return {
		isValid: true,
		date,
		shouldReset: false,
	};
};
